import React from 'react';
import { shape } from 'prop-types';
import classNames from 'classnames';
import { LazyHydrate as Hydrator } from 'nordic/lazy';
import StoreHeader from '../../features/store-header/store-header';
import Gallery from '../../features/gallery/gallery.desktop';
import Onboarding from '../../features/onboarding/onboarding.desktop';

// Head Section
import GroupedHeader from './sections/grouped-header';

import ItemStatusMessage from '../../features/item-status/head';
import RedirectFromStatus from '../../features/item-status/redirected-from';
import RelatedSearches from '../../features/related-searches';
import KYCNotification from '../../features/kyc-notification/kyc-notification.desktop';

// Short Description
import Bookmark from '../../features/bookmark';

import Header from '../../features/header/header.desktop';
import SellerValidated from '../../features/seller-validated/seller-validated';
import VerifiedVehicles from '../../features/verified-vehicles-tag/verified-vehicles-tag.desktop';
import InspectionVehicles from '../../features/inspection-vehicles-tag';
import Price from '../../features/price/price.desktop';
import GoodPriceTag from '../../features/good-price-tag/good-price-tag';
import SalesTerms from '../../features/sales-terms-short-description/sales-terms-short-description.desktop';
import ItemStatusShortDescription from '../../features/item-status/short-description';
import CreditsOfferBenefits from '../../features/credits-offer-benefits';
import CreditsOfferValue from '../../features/credits-offer-value';
import CreditsOfferInfoLabel from '../../features/credits-offer-info-label';
import FinanceableTooltip from '../../features/tooltips/financeable-tooltip';
import DistanceWithBuyer from '../../features/distance-with-buyer';
import GroupedMainActions from '../../features/grouped-main-actions';
import BuyBenefits from '../../features/buy-benefits/buy-benefits.desktop';
import VehicleReservation from '../../features/vehicle-reservation/return-policy/desktop';
import VehicleReservationLegacy from '../../features/vehicle-reservation-legacy/description/desktop';
import ReportProblem from '../../features/report-problem/report-problem.desktop';
import BoxCreditProposal from '../../features/box-credit-proposal';
import ActionLabel from '../../features/action-label/action-label.desktop';
import SellerProfileHighlighted from '../../features/seller-profile-highlighted/seller-profile-highlighted';
import SellerGoodAttentionHighlighted from '../../features/seller-good-attention/seller-good-attention-highlighted';

// Content Left
import VehicleHistorySpecs from '../../features/vehicle-history-specs';

import VehicleInspectionSpecs from '../../features/vehicle-inspection-specs';
import VehicleInspectionFullSpecs from '../../features/vehicle-inspection-full-specs';
import VehicleVerificationReport from '../../features/vehicle-verification-report';
import VehicleCondition from '../../features/vehicle-condition';
import HighlightedSpecsAttrs from '../../features/highlighted-specs/highlighted-specs-attrs.desktop';
import HighlightedSaleSpecs from '../../features/highlighted-sale-specs';
import TechnicalSpecifications from '../../features/technical-specifications/technical-specifications.desktop';
import Description from '../../features/description/description.desktop';
import Questions from '../../features/questions-vis/questions-vis.desktop';
import QuestionsUnregistered from '../../features/questions/questions-unregistered-vip';

// Content Right
import StickyWrapper from '../../features/sticky-wrapper';

import PriceComparison from '../../features/price-comparison';
import ExternalCredits from '../../features/external-credits';
import PublishVehicleCta from '../../features/publish-vehicle-cta';
import SellerReputation from '../../features/seller-reputation/seller-reputation';
import SellerProfile from '../../features/seller-profile/seller-profile';
import ReservationInfo from '../../features/reservation-info';
import SecurityTips from '../../features/security-tips/security-tips.desktop';
import RelatedServices from '../../features/related-services';
import QuoterMotors from '../../features/quoter-motors';
import Highlights from '../../features/highlights/highlights';
import VehiclePhysicalCondition from '../../features/vehicle-physical-condition';

// Recommendations
import RecommendationsCarouselSnapped from '../../features/recommendations/carousel-snapped';

import RecommendationsListHorizontal from '../../features/recommendations/list-horizontal';
import RecommendationsCarouselSnappedHorizontal from '../../features/recommendations/carousel-snapped-horizontal';

// Content Bottom
import Denounce from '../../features/denounce';

import BillboardAd from '../../features/billboard-ad';
import BillboardAdn from '../../features/billboard-adn/billboard-adn-bottom';
import SmartTabs from '../../features/smart-tabs/smart-tabs';
import ExtendedMenu from '../../features/extended-menu/extended-menu.desktop';

// General
import View from '../view';

import Snackbar from '../../features/snackbar';
import FinanciableVehicles from '../../features/financiable-vehicles-label/financiable-vehicles-label.desktop';
import GroupedShareBookmark from '../../features/grouped-share-bookmark';
import GiftRegistry from '../../features/gift-registry';
import Grid from '../../../../../components/grid';

const namespace = 'ui-vip-core-container';
const MotorsDesktop = props => (
  <View {...props} vertical="motors">
    <Onboarding />
    <Grid.Container className="ui-pdp-container--top">
      <Grid.Col className={`${namespace}--head`}>
        <RelatedSearches />
        <RecommendationsListHorizontal
          featureName="vis_carousel_cheaper"
          className="pt-12 pb-4 pl-0 pr-0"
          lazy={false}
          preload
          fetchPriority="high"
          criticalImages={3}
        />
        <RecommendationsCarouselSnappedHorizontal
          className="ui-recommendations-list__items-wrapper--horizontal carousel-classifieds-motors-vip-display-block"
          featureName="carousel_classifieds_motors_vip_top"
          lazy={false}
          preload
          fetchPriority="high"
          slidesPerView={3}
          criticalImages={3}
          arrowsSize="small"
        />
        <GroupedHeader />
        <RedirectFromStatus />
        <ItemStatusMessage />
        <RecommendationsCarouselSnapped
          featureName="carousel_classifieds_motors_vip_paused"
          className="mb-24 mt-24"
          lazy={false}
          hasDynamicSlidesPerView
          preload
          fetchPriority="high"
          criticalImages={5}
        />
        <KYCNotification />
      </Grid.Col>
    </Grid.Container>
    <Grid.Container>
      <StoreHeader />
    </Grid.Container>
    <Grid.Container className="ui-pdp-container--pdp">
      <Grid.Row id="ui-pdp-main-container" className="ui-pdp-container__row--reverse ui-pdp--relative  pb-40">
        <Grid.Col className="ui-pdp-container--column-right mt-24 pr-24">
          <StickyWrapper columnName="right">
            <Grid.Row className="ui-pdp-component-list pr-16 pl-16">
              <Grid.Col
                size={2}
                className={classNames(`${namespace}--short-description`, `${namespace}--column__right`)}
              >
                <Header>
                  <Bookmark showLabel={false} />
                </Header>
                <CreditsOfferInfoLabel featureName="item_proximity" />
                <SellerValidated />
                <VerifiedVehicles />
                <InspectionVehicles />
                <Price />
                <BoxCreditProposal />
                <ActionLabel className="ui-b2c-info" />
                <GoodPriceTag />
                <FinanciableVehicles />
                <SalesTerms />
                <Highlights className="ui-pdp-highlights" />
                <ItemStatusShortDescription />
                <CreditsOfferValue />
                <CreditsOfferBenefits />
                <FinanceableTooltip />
                <VehicleReservation />
                <VehicleReservationLegacy />
                <DistanceWithBuyer />
                <GroupedMainActions />
                <SellerGoodAttentionHighlighted />
                <CreditsOfferInfoLabel featureName="credits_offer_landing" />
                <BuyBenefits />
                <ReportProblem />
                <GiftRegistry className="mt-24" />
                <GroupedShareBookmark className="mt-24" showOnlyForWishlistVariant />
              </Grid.Col>
            </Grid.Row>
            <Grid.Row className="ui-pdp-component-list ui-vip-content-right mt-16">
              <Hydrator whenIdle>
                <PriceComparison />
              </Hydrator>
              <ExternalCredits />
              <SellerProfileHighlighted />
              <SellerProfile />
              <SellerReputation />
              <ReservationInfo />
              <RelatedServices />
              <QuoterMotors />
              <PublishVehicleCta />
              <SecurityTips />
            </Grid.Row>
          </StickyWrapper>
        </Grid.Col>
        <Grid.Col className="pb-40" size={2}>
          <Grid.Row>
            <Grid.Col size={2}>
              <Gallery />
            </Grid.Col>
          </Grid.Row>
          <Grid.Col className={`${namespace}--content-left`}>
            <RecommendationsCarouselSnapped
              featureName="vis_similar_financial_offer_carousel"
              className="ui-pdp-vis-carousel-b2c"
              slidesPerView={3}
              lazy={false}
            />
            <RecommendationsCarouselSnapped
              className="ui-pdp-vis-carousel-b2c"
              featureName="carousel_classifieds_motors_vip_bau"
              lazy={false}
              slidesPerView={3}
            />
            <RecommendationsCarouselSnapped
              className="ui-pdp-vis-carousel-b2c"
              featureName="carousel_classifieds_motors_vip_fsbo"
              lazy={false}
              slidesPerView={3}
            />
            <RecommendationsCarouselSnapped
              featureName="vis_similar_financial_motors_carousel"
              className="ui-pdp-vis-carousel-b2c"
              slidesPerView={3}
              lazy={false}
            />
            <VehiclePhysicalCondition />
            <VehicleCondition />
            <VehicleVerificationReport />
            <VehicleHistorySpecs />
            <VehicleInspectionSpecs />
            <VehicleInspectionFullSpecs />
            <RecommendationsCarouselSnapped
              className="ui-pdp-vis-carousel-b2c vis-vpp-carousel"
              featureName="vis_vpp_carousel"
              slidesPerView={3}
            />
            <HighlightedSaleSpecs />
            <TechnicalSpecifications />
            <HighlightedSpecsAttrs className="pl-0 pr-0" />
            <Description />
            <RecommendationsCarouselSnapped
              featureName="carousel_classifieds_motors_vip_historical_securedloan"
              className="ui-pdp-vis-carousel-b2c motors-historical-securedloan"
              slidesPerView={3}
              lazy={false}
            />
            <Questions enableRedirectAction />
            <QuestionsUnregistered className="ui-pdp-questions-unregistered__desktop" />
          </Grid.Col>
        </Grid.Col>
      </Grid.Row>
    </Grid.Container>
    <Grid.Container className="ui-vip-core ui-pdp-container--bottom">
      <Denounce />
      <RecommendationsCarouselSnapped
        featureName="carousel_classifieds_motors_vip"
        className="mb-30 mt-20"
        hasDynamicSlidesPerView
      />
      <RecommendationsCarouselSnapped
        featureName="carousel_classifieds_motors_vip_seller_items"
        className="mb-30 mt-20"
        hasDynamicSlidesPerView
      />
      <BillboardAd />
      <BillboardAdn />
      <SmartTabs />
      <ExtendedMenu />
    </Grid.Container>
    <Snackbar />
  </View>
);

MotorsDesktop.propTypes = {
  initialState: shape().isRequired,
};

export default MotorsDesktop;
